import { useModel as _useModel, defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, resolveComponent as _resolveComponent, createBlock as _createBlock, vModelText as _vModelText, withModifiers as _withModifiers, withKeys as _withKeys, withDirectives as _withDirectives, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, createVNode as _createVNode, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { class: "emailSettingPreferencesEmailsInput__tags" }
const _hoisted_2 = ["placeholder", "onKeydown"]
const _hoisted_3 = {
  key: 1,
  class: "emailSettingPreferencesEmailsInput__actions"
}

import { ref, computed, inject, nextTick, watch } from 'vue';
import { CompassEnums } from '@sellsy/compass2';
import { Translator } from 'types/injects/translator.type';
import { validateEmail } from 'Helpers/validator/EmailValidator';

const MAX_EMAILS = 20;

interface Email {
    value: string;
    isValid: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'EmailSettingPreferencesEmailsInput',
  props: {
    "modelValue": { default: () => [] },
    "modelModifiers": {},
  },
  emits: ["update:modelValue"],
  setup(__props) {

const modelValue = _useModel<string[]>(__props, "modelValue");

const $t = inject('$t') as Translator;

const emails = ref<Email[]>([]);
const inputValue = ref('');
const inputRef = ref<HTMLInputElement | null>(null);
const isEditing = ref(false);

const isValidEmail = (email: string): boolean => {
    return validateEmail(email.trim());
};

const toEmailObjects = (values: string[]): Email[] => {
    return values.map((value) => ({ value, isValid: isValidEmail(value) }));
};

const getValidEmailValues = (): string[] => {
    return emails.value.filter((email) => email.isValid).map((email) => email.value);
};

const hasChanged = (newEmails: string[]): boolean => {
    if (newEmails.length !== modelValue.value.length) {
        return true;
    }
    const sortedNew = [...newEmails].sort();
    const sortedOriginal = [...modelValue.value].sort();
    return sortedNew.some((email, index) => email !== sortedOriginal[index]);
};

const hasError = computed(() => emails.value.some((email) => !email.isValid));

const hasInvalidInput = computed(() => {
    const trimmed = inputValue.value.trim();
    return trimmed !== '' && !isValidEmail(trimmed);
});

const isLimitExceeded = computed(() => emails.value.length > MAX_EMAILS);

const isExceeding = (index: number): boolean => {
    return index >= MAX_EMAILS;
};

const counterMessage = computed(() => {
    const params = { count: String(emails.value.length), max: String(MAX_EMAILS) };
    if (isLimitExceeded.value) {
        return $t.__('email.EmailSettingPreferences.emailsAddedToCC.emailCountExceeded', params);
    }
    return $t.n__(
        'email.EmailSettingPreferences.emailsAddedToCC.emailCount',
        null,
        emails.value.length,
        params,
    );
});

const canSave = computed(() => {
    return !hasError.value && !hasInvalidInput.value && !isLimitExceeded.value;
});

watch(
    () => modelValue.value,
    (newValue) => {
        if (!isEditing.value) {
            emails.value = toEmailObjects(newValue);
        }
    },
    { immediate: true },
);

const addEmailToList = (value: string) => {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
        return;
    }

    // Check if email already exists
    if (emails.value.some((email) => email.value.toLowerCase() === trimmedValue.toLowerCase())) {
        return;
    }

    emails.value.push({
        value: trimmedValue,
        isValid: isValidEmail(trimmedValue),
    });
};

const addEmail = () => {
    if (inputValue.value.trim()) {
        addEmailToList(inputValue.value);
        inputValue.value = '';
    }
};

const handlePaste = (event: ClipboardEvent) => {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';
    const separatorRegex = /[,;\s]+/;
    const emailsList = pastedText.split(separatorRegex).filter((email) => email.trim());

    for (const email of emailsList) {
        addEmailToList(email);
    }
    inputValue.value = '';
};

const removeEmail = (emailValue: string) => {
    emails.value = emails.value.filter((email) => email.value !== emailValue);

    if (!isEditing.value) {
        modelValue.value = getValidEmailValues();
    }
};

const focusInput = () => {
    if (!isEditing.value) {
        enterEditMode();
    } else {
        inputRef.value?.focus();
    }
};

const enterEditMode = () => {
    isEditing.value = true;
    nextTick(() => inputRef.value?.focus());
};

const save = () => {
    addEmail();
    isEditing.value = false;
    const validEmails = getValidEmailValues();
    if (hasChanged(validEmails)) {
        modelValue.value = validEmails;
    }
};

const cancel = () => {
    emails.value = toEmailObjects(modelValue.value);
    inputValue.value = '';
    isEditing.value = false;
};

return (_ctx: any,_cache: any) => {
  const _component_CompassTagInteractive = _resolveComponent("CompassTagInteractive")!
  const _component_CompassButton = _resolveComponent("CompassButton")!
  const _component_CompassIconButton = _resolveComponent("CompassIconButton")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("div", {
      class: _normalizeClass(["emailSettingPreferencesEmailsInput__row", { 'emailSettingPreferencesEmailsInput__row--editing': isEditing.value }])
    }, [
      (isEditing.value || emails.value.length > 0)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: _normalizeClass(["emailSettingPreferencesEmailsInput__field", {
                    'emailSettingPreferencesEmailsInput__field--error': hasError.value,
                    'emailSettingPreferencesEmailsInput__field--editing': isEditing.value,
                }]),
            onClick: focusInput
          }, [
            _createElementVNode("div", _hoisted_1, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(emails.value, (email, index) => {
                return (_openBlock(), _createBlock(_component_CompassTagInteractive, {
                  key: email.value,
                  size: _unref(CompassEnums).TagInteractiveSize.SMALL,
                  color: 
                            email.isValid && !isExceeding(index)
                                ? _unref(CompassEnums).TagInteractiveColorType.STROKED
                                : _unref(CompassEnums).TagInteractiveColorType.RED
                        ,
                  modelValue: email.value,
                  valueLabel: email.value,
                  onTagRemove: ($event: any) => (removeEmail(email.value))
                }, null, 8 /* PROPS */, ["size", "color", "modelValue", "valueLabel", "onTagRemove"]))
              }), 128 /* KEYED_FRAGMENT */)),
              (isEditing.value)
                ? _withDirectives((_openBlock(), _createElementBlock("input", {
                    key: 0,
                    ref_key: "inputRef",
                    ref: inputRef,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((inputValue).value = $event)),
                    type: "text",
                    class: "emailSettingPreferencesEmailsInput__input saas-custom-input",
                    placeholder: 
                            emails.value.length === 0
                                ? _unref($t).__('email.EmailSettingPreferences.emailsAddedToCC.placeholder')
                                : ''
                        ,
                    onKeydown: [
                      _withKeys(_withModifiers(addEmail, ["prevent"]), ["enter"]),
                      _withKeys(_withModifiers(addEmail, ["prevent"]), [","]),
                      _withKeys(_withModifiers(addEmail, ["prevent"]), [";"])
                    ],
                    onPaste: handlePaste
                  }, null, 40 /* PROPS, NEED_HYDRATION */, _hoisted_2)), [
                    [_vModelText, inputValue.value]
                  ])
                : _createCommentVNode("v-if", true),
              (!isEditing.value)
                ? (_openBlock(), _createBlock(_component_CompassButton, {
                    key: 1,
                    class: "emailSettingPreferencesEmailsInput__editButton",
                    label: 
                            _unref($t).__('email.EmailSettingPreferences.emailsAddedToCC.editEmailsButton')
                        ,
                    leftIcon: "edit",
                    size: _unref(CompassEnums).ButtonSize.SMALL,
                    type: _unref(CompassEnums).ButtonType.TERTIARY,
                    onClick: enterEditMode
                  }, null, 8 /* PROPS */, ["label", "size", "type"]))
                : _createCommentVNode("v-if", true)
            ])
          ], 2 /* CLASS */))
        : _createCommentVNode("v-if", true),
      (isEditing.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createVNode(_component_CompassButton, {
              label: _unref($t).__('common.actions.save'),
              size: _unref(CompassEnums).ButtonSize.MEDIUM,
              disabled: !canSave.value,
              onClick: save
            }, null, 8 /* PROPS */, ["label", "size", "disabled"]),
            _createVNode(_component_CompassIconButton, {
              icon: "close",
              size: _unref(CompassEnums).IconButtonSize.MEDIUM,
              onClick: cancel
            }, null, 8 /* PROPS */, ["size"])
          ]))
        : _createCommentVNode("v-if", true),
      (!isEditing.value && emails.value.length === 0)
        ? (_openBlock(), _createBlock(_component_CompassButton, {
            key: 2,
            label: _unref($t).__('email.EmailSettingPreferences.emailsAddedToCC.addEmailButton'),
            leftIcon: "plus",
            size: _unref(CompassEnums).ButtonSize.SMALL,
            type: _unref(CompassEnums).ButtonType.TERTIARY,
            onClick: enterEditMode
          }, null, 8 /* PROPS */, ["label", "size", "type"]))
        : _createCommentVNode("v-if", true)
    ], 2 /* CLASS */),
    (isEditing.value)
      ? (_openBlock(), _createElementBlock("p", {
          key: 0,
          class: _normalizeClass(["emailSettingPreferencesEmailsInput__counter", { 'emailSettingPreferencesEmailsInput__counter--exceeded': isLimitExceeded.value }])
        }, _toDisplayString(counterMessage.value), 3 /* TEXT, CLASS */))
      : _createCommentVNode("v-if", true)
  ]))
}
}

})